import { useSelector } from "react-redux";
import tenancyAdminItems from './tenancyAdminItems';
import adminItems from './adminItems';
import userItems from "./userItems";
import { getTrillionItems } from "./trillionItems";
import { ENVIRONMENT_NAME, isReportsBBrand } from "../config";

const useMenuItems = () => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const { role, token } = user;
    const { isHierarchyTenancy, featureSwitches: actualData, featureSwitchesLoading } = useSelector((state) => state.tenancy);

    //get url for trillion button
    const trillionItems = getTrillionItems(token);

    // Deep clone an object to ensure we don't modify the original
    const deepClone = (obj) => {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }

        const clone = Array.isArray(obj) ? [] : {};

        for (let key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                clone[key] = deepClone(obj[key]);
            }
        }

        return clone;
    };

    // Recursive filter function to handle both menu items and nested children
    const filterMenuItem = (item, user, userRole) => {
        if (!item) return null;

        // Create a copy to avoid modifying the original
        const itemCopy = deepClone(item);
        // Handle group type
        if (itemCopy.type === 'group') {
            if (!itemCopy.children || !Array.isArray(itemCopy.children)) {
                return itemCopy;
            }

            // Filter children of group
            const filteredChildren = itemCopy.children
                .map(child => filterMenuItem(child, user, userRole))
                .filter(Boolean); // Remove falsy values

            itemCopy.children = filteredChildren;
            return itemCopy;
        }

        // Handle item type
        if (itemCopy.type === 'item') {
            //console.log("Processing menu item:", itemCopy.id, itemCopy.title);

            // Main Settings feature switches
            if (itemCopy.id === 'dashboard1') {
                return actualData?.enableDashboard ? itemCopy : null;
            }

            if (itemCopy.id === 'feed1') {
                // Show News Feed based on feature flag
                return actualData?.enableNewsFeed ? itemCopy : null;
            }
            if (itemCopy.id === 'tenancy1' && userRole !== "") {
                // Show Settings based on feature flag
                return actualData?.enableSettings ? itemCopy : null;
            }
            if (itemCopy.id === 'help' && userRole !== "") {
                // Show Help based on feature flag
                return actualData?.enableHelp ? itemCopy : null;
            }

            if (itemCopy.id === 'trillion' &&  ["TenancyAdministrators", "Administrators"].some(role => userRole.includes(role))) {
                // Show Trillion based on feature flag
                actualData?.enableTrillion  ? itemCopy.disabled = false : itemCopy.disabled = true;
                return actualData?.showTrillion ? itemCopy : null;
            }

            if (itemCopy.id === 'reports1') {
                // Show Reports tab based on tenancy feature flag
                if (userRole === "") {
                    return actualData?.enableReporting && user.reportingLevel !== "" && user.enableReporting ? itemCopy : null;
                }
                return actualData?.enableReporting ? itemCopy : null;
            }
            if (itemCopy.id === 'reports-b') {
                // Reports B: offered on the SAT brands (boxphish + Coro + CensorNet/TrustLayer,
                // see isReportsBBrand), gated by its own enableReportsB switch independent of the
                // QuickSight enableReporting flag. Which tenancies get it is controlled purely by
                // flipping enableReportsB per tenancy.
                //   - super admins + tenancy admins: full (un-scoped) access — unchanged.
                //   - regular users (role === '') with a reportingLevel assigned: a locked,
                //     self-scoped view (BD-901). The page enforces the lock; the server
                //     enforces the actual data scope from the caller's identity.
                //   - regular users without a level, and partner admins, never see it.
                if (!isReportsBBrand()) return null;
                if (!actualData?.enableReportsB) return null;
                if (userRole === "Administrators" || (userRole || "").includes("TenancyAdministrators")) return itemCopy;
                if (userRole === "" && user.reportingLevel != null && user.reportingLevel !== "") return itemCopy;
                return null;
            }
            if (itemCopy.id === 'ondemand1') {
                // Show OnDemand tab based on tenancy feature flag
                // If still loading, hide the tab until we know for sure

                if (featureSwitchesLoading || !actualData) {
                    //console.log("OnDemand Menu Decision: Loading or no data, hiding tab");
                    return null;
                }

                const shouldShow = actualData.enableOndemand === true;
                // console.log("OnDemand Menu Decision:", {
                //    enableOndemand: actualData.enableOndemand,
                ///   shouldShow,
                //  isLoading: featureSwitchesLoading
                // });

                if (userRole === "") {
                    //return shouldShow && user.onDemandLevel !== "" && user.enableOndemand ? itemCopy : null;
                    return actualData?.enableOndemand && user.onDemandLevel !== "" && user.enableOndemand ? itemCopy : null;
                }
                return shouldShow ? itemCopy : null;
            }
            if (itemCopy.id === 'learning-journeys1') {
                // Show Journey Builder tab based on tenancy feature flag
                return actualData?.enableJourneyBuilder ? itemCopy : null;
            }
            if (itemCopy.id === 'tenants1') {
                // Check both role permissions AND feature flag
                let hasRoleAccess = false;
                if (userRole.includes("PartnerAdministrators")) hasRoleAccess = true;
                else if (userRole.includes("TenancyAdministrators")) hasRoleAccess = isHierarchyTenancy;
                else if (userRole === "Administrators") hasRoleAccess = true;

                // Show only if both role access AND feature flag are enabled
                return (hasRoleAccess && actualData?.enableTenants) ? itemCopy : null;
            }

            // Allow access based on role - full admins get everything, tenancy admins get limited access
            if (userRole === "Administrators" || userRole.includes("TenancyAdministrators")) {
                return itemCopy;
            }
            return null;
        }

        // Handle collapse type
        if (itemCopy.type === 'collapse') {
            // Check if this is the Content collapse item - it should be hidden if enableContent is false
            if (itemCopy.id === 'content1') {
                if (!actualData?.enableContent) {
                    //console.log("Content Menu Decision: enableContent is false, hiding entire content section");
                    return null;
                }
            }

            if (!itemCopy.children || !Array.isArray(itemCopy.children)) {
                return itemCopy;
            }

            // Filter the collapse children - this will apply feature flag filtering to nested items
            const filteredChildren = itemCopy.children
                .map(child => {
                    // console.log("Processing collapse child:", child.id, child.title);

                    // Apply feature flag filtering to collapse children
                    if (child.id === 'ondemand1') {
                        if (featureSwitchesLoading || !actualData) {
                            //console.log("OnDemand Menu Decision (in collapse): Loading or no data, hiding tab");
                            return null;
                        }
                        const shouldShow = actualData.enableOndemand === true;
                        // console.log("OnDemand Menu Decision (in collapse):", {
                        //    enableOndemand: actualData.enableOndemand,
                        //   shouldShow,
                        //   isLoading: featureSwitchesLoading
                        //});

                        if (userRole === "") {
                            return shouldShow && user.onDemandLevel !== "" && user.enableOndemand ? child : null
                        }

                        return shouldShow ? child : null;
                    }

                    if (child.id === 'learning-journeys1') {
                        const shouldShow = actualData?.enableJourneyBuilder === true && user.role !== "";
                        // console.log("Journey Builder Menu Decision (in collapse):", {
                        //    enableJourneyBuilder: actualData?.enableJourneyBuilder,
                        //    shouldShow
                        //});
                        return shouldShow ? child : null;
                    }

                    if (child.id === 'packs1') {
                        const shouldShow = actualData?.enableContentPacks === true && user.role !== "";
                        //console.log("Content Packs Menu Decision (in collapse):", {
                        //   enableContentPacks: actualData?.enableContentPacks,
                        //    shouldShow
                        //});
                        return shouldShow ? child : null;
                    }

                    if (child.id === 'simulations1') {
                        const shouldShow = actualData?.enableSimulation === true && user.role !== "";
                        //console.log("Content Packs Menu Decision (in collapse):", {
                        //   enableContentPacks: actualData?.enableContentPacks,
                        //    shouldShow
                        //});
                        return shouldShow ? child : null;
                    }

                    // For other children, apply normal filtering
                    return filterMenuItem(child, user, userRole);
                })
                .filter(Boolean); // Remove null or undefined values

            // Only include this collapse item if it has at least one valid child
            if (filteredChildren.length > 0) {
                itemCopy.children = filteredChildren;
                return itemCopy;
            }

            return null; // Don't include collapse items with no children
        }

        // Default case: return the item unchanged
        return itemCopy;
    };

    const filterUserMenuItems = (item, user, userRole) => {
        if (!item) return null;

        // Create a copy to avoid modifying the original
        const itemCopy = deepClone(item);
        // Handle group type
        if (itemCopy.type === 'group') {
            if (!itemCopy.children || !Array.isArray(itemCopy.children)) {
                return itemCopy;
            }

            // Filter children of group
            const filteredChildren = itemCopy.children
                .map(child => filterUserMenuItems(child, user, userRole))
                .filter(Boolean); // Remove falsy values

            itemCopy.children = filteredChildren;
            return itemCopy;
        }

        // Handle item type
        if (itemCopy.type === 'item') {

            // show news feed based on the enableNewsFeedValue
            if (itemCopy.id === 'feed1') {
                return actualData?.enableNewsFeed ? itemCopy : null;
            }
            if (itemCopy.id === 'dashboard1') {
                return actualData?.enableDashboard ? itemCopy : null;
            }
            else {
                return itemCopy
            }
        }

        return itemCopy;

    }


    // Apply feature flag filtering to userItems as well
    const filteredUserItems = filterUserMenuItems(deepClone(userItems), user, role);
    const items = filteredUserItems ? [filteredUserItems] : [];

    // Add menu items based on user role
    if (role === 'Administrators') {
        // For full admins, still apply feature flag filtering but keep role-based access
        const filteredTenancyAdminItems = filterMenuItem(deepClone(tenancyAdminItems), user, role);
        const filteredTrillionItems = filterMenuItem(deepClone(trillionItems), user, role);
        const filteredAdminItems = filterMenuItem(deepClone(adminItems), user, role);

        if (filteredTenancyAdminItems &&
            filteredTenancyAdminItems.children &&
            filteredTenancyAdminItems.children.length > 0) {
            items.push(filteredTenancyAdminItems);
        }

        if (filteredTrillionItems &&
            filteredTrillionItems.children &&
            filteredTrillionItems.children.length > 0) {
            items.push(filteredTrillionItems);
        }

        if (filteredAdminItems &&
            filteredAdminItems.children &&
            filteredAdminItems.children.length > 0) {
            items.push(filteredAdminItems);
        }
    } else if (role.includes('TenancyAdministrators') || role === '') {
        // For tenancy admins, filter the items
        const filteredTenancyAdminItems = filterMenuItem(deepClone(tenancyAdminItems), user, role);
        const filteredTrillionItems = filterMenuItem(deepClone(trillionItems), user, role);

        // Add the filtered items if it's valid
        if (filteredTenancyAdminItems &&
            filteredTenancyAdminItems.children &&
            filteredTenancyAdminItems.children.length > 0) {
            items.push(filteredTenancyAdminItems);
        }

        if (filteredTrillionItems &&
            filteredTrillionItems.children &&
            filteredTrillionItems.children.length > 0) {
            items.push(filteredTrillionItems);
        }
    }

    return { items };
};

export default useMenuItems;